import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Package, Plus, Search, Edit, Trash2, AlertTriangle, Building2, ArrowUpDown, Download, FileText, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PinModal from '../components/PinModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'consumable', label: 'Consumable' },
  { value: 'medication', label: 'Medication' },
  { value: 'lab_supply', label: 'Lab Supply' },
  { value: 'other', label: 'Other' }
];

const Inventory = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: 'consumable',
    unit_of_measure: 'pcs',
    current_quantity: 0,
    moq: 10,
    preferred_vendor_id: '',
    last_cost_price: 0,
    selling_price: 0,
    is_billable: false,
    is_active: true
  });

  const [vendorForm, setVendorForm] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  const [adjustForm, setAdjustForm] = useState({
    quantity_change: 0,
    movement_type: 'manual_adjustment',
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  const fetchData = async () => {
    try {
      const [itemsRes, vendorsRes, countRes] = await Promise.all([
        axios.get(`${API}/inventory?include_inactive=${showInactive}`),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/inventory/low-stock/count`)
      ]);
      setItems(itemsRes.data);
      setVendors(vendorsRes.data);
      setLowStockCount(countRes.data.count);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData };
      if (!payload.preferred_vendor_id) delete payload.preferred_vendor_id;
      if (!payload.sku) delete payload.sku; // Let backend auto-generate

      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, payload);
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API}/inventory`, payload);
        toast.success('Item created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/vendors`, vendorForm);
      toast.success('Vendor created successfully');
      setShowVendorModal(false);
      setVendorForm({ name: '', contact_person: '', phone: '', email: '', address: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustStock = async (pin) => {
    if (!adjustingItem) return;
    setSaving(true);
    try {
      await axios.post(`${API}/inventory/${adjustingItem.id}/adjust`, {
        ...adjustForm,
        pin
      });
      toast.success('Stock adjusted successfully');
      setShowPinModal(false);
      setShowAdjustModal(false);
      setAdjustingItem(null);
      setAdjustForm({ quantity_change: 0, movement_type: 'manual_adjustment', note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Deactivate ${item.name}?`)) return;
    try {
      await axios.delete(`${API}/inventory/${item.id}`);
      toast.success('Item deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate item');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', sku: '', category: 'consumable', unit_of_measure: 'pcs',
      current_quantity: 0, moq: 10, preferred_vendor_id: '', last_cost_price: 0,
      selling_price: 0, is_billable: false, is_active: true
    });
    setEditingItem(null);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      sku: item.sku,
      category: item.category,
      unit_of_measure: item.unit_of_measure,
      current_quantity: item.current_quantity,
      moq: item.moq,
      preferred_vendor_id: item.preferred_vendor_id || '',
      last_cost_price: item.last_cost_price,
      selling_price: item.selling_price,
      is_billable: item.is_billable,
      is_active: item.is_active
    });
    setShowModal(true);
  };

  const openAdjustModal = (item) => {
    setAdjustingItem(item);
    setAdjustForm({ quantity_change: 0, movement_type: 'manual_adjustment', note: '' });
    setShowAdjustModal(true);
  };

  const filteredItems = items
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];
      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();
      if (sortDir === 'asc') return valA > valB ? 1 : -1;
      return valA < valB ? 1 : -1;
    });

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  if (user?.role !== 'owner') {
    return (
      <Layout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold text-slate-800">Access Restricted</h2>
          <p className="text-slate-600">Only Owner Admin can access inventory management.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="inventory-page">
        <div className="page-header mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="page-title">Inventory Management</h1>
                <p className="text-slate-500">{items.length} items total</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {lowStockCount > 0 && (
                <a href="/inventory/low-stock" className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 hover:bg-amber-100">
                  <AlertTriangle className="w-4 h-4" />
                  {lowStockCount} Low Stock
                </a>
              )}
              <Button variant="outline" onClick={() => setShowVendorModal(true)}>
                <Building2 className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
              <Button onClick={() => { resetForm(); setShowModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch checked={showInactive} onCheckedChange={setShowInactive} />
              <Label>Show Inactive</Label>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No inventory items found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600 cursor-pointer" onClick={() => toggleSort('name')}>
                      <span className="flex items-center gap-1">Item <ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                    <th className="text-left p-3 font-medium text-slate-600">SKU</th>
                    <th className="text-left p-3 font-medium text-slate-600">Category</th>
                    <th className="text-center p-3 font-medium text-slate-600 cursor-pointer" onClick={() => toggleSort('current_quantity')}>
                      <span className="flex items-center justify-center gap-1">Qty <ArrowUpDown className="w-3 h-3" /></span>
                    </th>
                    <th className="text-center p-3 font-medium text-slate-600">MOQ</th>
                    <th className="text-right p-3 font-medium text-slate-600">Cost</th>
                    <th className="text-right p-3 font-medium text-slate-600">Price</th>
                    <th className="text-center p-3 font-medium text-slate-600">Billable</th>
                    <th className="text-center p-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.id} className={`border-b hover:bg-slate-50 ${!item.is_active ? 'opacity-50' : ''}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {item.current_quantity <= item.moq && item.is_active && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" title="Low Stock" />
                          )}
                          <span className="font-medium text-slate-800">{item.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-slate-600 text-sm font-mono">{item.sku}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded capitalize">
                          {item.category.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`font-semibold ${item.current_quantity <= item.moq ? 'text-amber-600' : 'text-slate-800'}`}>
                          {item.current_quantity}
                        </span>
                        <span className="text-slate-500 text-sm ml-1">{item.unit_of_measure}</span>
                      </td>
                      <td className="p-3 text-center text-slate-600">{item.moq}</td>
                      <td className="p-3 text-right text-slate-600">${item.last_cost_price.toFixed(2)}</td>
                      <td className="p-3 text-right text-slate-800 font-medium">${item.selling_price.toFixed(2)}</td>
                      <td className="p-3 text-center">
                        {item.is_billable ? (
                          <Check className="w-4 h-4 text-green-600 mx-auto" />
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openAdjustModal(item)} title="Adjust Stock">
                            <ArrowUpDown className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditModal(item)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          {item.is_active && (
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(item)} title="Deactivate" className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add/Edit Item Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Item' : 'Add Inventory Item'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Item Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div>
                  <Label>SKU {!editingItem && '(auto-generated if blank)'}</Label>
                  <Input value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} disabled={!!editingItem} className={editingItem ? 'bg-slate-100' : ''} />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit of Measure *</Label>
                  <Input value={formData.unit_of_measure} onChange={(e) => setFormData({...formData, unit_of_measure: e.target.value})} required />
                </div>
                <div>
                  <Label>Current Quantity</Label>
                  <Input type="number" step="0.01" value={formData.current_quantity} onChange={(e) => setFormData({...formData, current_quantity: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>MOQ (Reorder Level)</Label>
                  <Input type="number" step="0.01" value={formData.moq} onChange={(e) => setFormData({...formData, moq: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Preferred Vendor</Label>
                  <Select value={formData.preferred_vendor_id || 'none'} onValueChange={(v) => setFormData({...formData, preferred_vendor_id: v === 'none' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select vendor..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {vendors.filter(v => v.is_active).map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Last Cost Price ($)</Label>
                  <Input type="number" step="0.01" value={formData.last_cost_price} onChange={(e) => setFormData({...formData, last_cost_price: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Selling Price ($)</Label>
                  <Input type="number" step="0.01" value={formData.selling_price} onChange={(e) => setFormData({...formData, selling_price: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="col-span-2 flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.is_billable} onCheckedChange={(v) => setFormData({...formData, is_billable: v})} />
                    <Label>Billable to Patient</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({...formData, is_active: v})} />
                    <Label>Active</Label>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Vendor Modal */}
        <Dialog open={showVendorModal} onOpenChange={setShowVendorModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Vendor</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleVendorSubmit} className="space-y-4">
              <div>
                <Label>Vendor Name *</Label>
                <Input value={vendorForm.name} onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})} required />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input value={vendorForm.contact_person} onChange={(e) => setVendorForm({...vendorForm, contact_person: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={vendorForm.phone} onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={vendorForm.email} onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Textarea value={vendorForm.address} onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})} rows={2} />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowVendorModal(false)} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Vendor
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Adjust Stock Modal */}
        <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Adjust Stock - {adjustingItem?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Current Quantity: <span className="font-semibold text-slate-800">{adjustingItem?.current_quantity} {adjustingItem?.unit_of_measure}</span></p>
              </div>
              <div>
                <Label>Adjustment Type</Label>
                <Select value={adjustForm.movement_type} onValueChange={(v) => setAdjustForm({...adjustForm, movement_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual_adjustment">Manual Adjustment</SelectItem>
                    <SelectItem value="restock">Restock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity Change (+/-)</Label>
                <Input type="number" step="0.01" value={adjustForm.quantity_change} onChange={(e) => setAdjustForm({...adjustForm, quantity_change: parseFloat(e.target.value) || 0})} placeholder="e.g., +10 or -5" />
                <p className="text-xs text-slate-500 mt-1">Use positive for additions, negative for deductions</p>
              </div>
              <div>
                <Label>Reason/Note</Label>
                <Textarea value={adjustForm.note} onChange={(e) => setAdjustForm({...adjustForm, note: e.target.value})} rows={2} placeholder="Reason for adjustment..." />
              </div>
              {adjustForm.quantity_change !== 0 && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    New Quantity: <span className="font-semibold">{(adjustingItem?.current_quantity || 0) + adjustForm.quantity_change} {adjustingItem?.unit_of_measure}</span>
                  </p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAdjustModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={() => setShowPinModal(true)} disabled={adjustForm.quantity_change === 0} className="flex-1">
                  Confirm Adjustment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* PIN Modal */}
        <PinModal
          isOpen={showPinModal}
          onClose={() => setShowPinModal(false)}
          onVerify={handleAdjustStock}
          title="Verify Stock Adjustment"
          description={`Enter your PIN to adjust ${adjustingItem?.name} by ${adjustForm.quantity_change > 0 ? '+' : ''}${adjustForm.quantity_change}`}
        />
      </div>
    </Layout>
  );
};

export default Inventory;
